import React from 'react';
import { absUrl } from '../lib/api';
import { formatMoney } from '../lib/utils';

/**
 * Product group card — renders one row per weight variant, matching Retailers UI:
 *  image (left, clickable) | category badge, big weight, "N flavours · from ₹X", flavour preview,
 *  MOQ line, red "CHOOSE FLAVOUR" button.
 *
 * Round 2 change: The product image is now clickable and opens the same
 * "Choose Flavour" popup as the red button.
 */
export default function ProductCard({ product, variant, categoryName, onChoose }) {
    // Face Image resolution (Batch B2 Bug-Fix Pass 3, Feature 2):
    // 1. If the admin has set a `face_image_url` on this variant, use it verbatim.
    // 2. Otherwise fall back to the first flavour's image (existing behaviour).
    // 3. Finally fall back to the product-level image if flavours have none.
    const previewImg = (variant.face_image_url && variant.face_image_url.trim())
        || variant.flavours?.find(f => f.image_url)?.image_url
        || product.image_url;
    const flavourNames = (variant.flavours || []).map(f => f.name);
    const preview = flavourNames.slice(0, 3).join(' · ') + (flavourNames.length > 3 ? '...' : '');
    const openPopup = () => onChoose({ product, variant });

    return (
        <div className="card-soft overflow-hidden flex" data-testid={`product-card-${product.id}-${variant.id}`}>
            <button
                type="button"
                onClick={openPopup}
                aria-label={`Open ${product.name} ${variant.size_label} — choose flavour`}
                className="relative w-32 sm:w-40 bg-gradient-to-br from-neutral-100 to-neutral-200 flex-shrink-0 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-sdf-red hover:brightness-95 transition-[filter] duration-200"
                data-testid={`product-image-${variant.id}`}
            >
                {previewImg ? (
                    <img src={absUrl(previewImg)} alt={product.name} className="w-full h-full object-cover pointer-events-none" />
                ) : (
                    <div className="w-full h-full min-h-[140px] flex items-center justify-center text-neutral-400 text-xs px-2 text-center">{product.name}</div>
                )}
            </button>
            <div className="flex-1 p-3 sm:p-4 flex flex-col gap-1.5">
                <div className="text-[10px] tracking-[0.15em] font-bold text-sdf-red">{(categoryName || product.category_slug).toUpperCase()}</div>
                <div className="text-xl sm:text-2xl font-extrabold text-sdf-text leading-tight">{variant.size_label}</div>
                <div className="text-xs text-sdf-text-2">{variant.flavours?.length || 0} flavours · from {formatMoney(variant.from_price || variant.price)}</div>
                <div className="text-xs text-sdf-text line-clamp-2">{preview}</div>
                {variant.moq > 1 && (
                    <div className="text-[10px] uppercase font-bold tracking-wider text-sdf-text-2">MOQ {variant.moq} units</div>
                )}
                <div className="mt-auto pt-2">
                    <button onClick={openPopup} className="btn-red" data-testid={`choose-flavour-${variant.id}`}>CHOOSE FLAVOUR</button>
                </div>
            </div>
        </div>
    );
}
